import { useEffect, useRef, useState } from "react";
import { KeyRound, LifeBuoy, LogOut, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Segundo fator na entrada.
 *
 * Ocupa a tela inteira e não tem como ser dispensada: sair daqui é verificar o
 * código, gastar um código de recuperação, ou sair da conta. Um desafio que se
 * pode fechar no X não é desafio.
 *
 * POR QUE O CÓDIGO DE RECUPERAÇÃO DESLIGA O FATOR
 * -----------------------------------------------
 *
 * O Supabase não deixa uma sessão `aal1` remover um fator verificado — e é
 * justamente nessa sessão que está quem perdeu o celular. O caminho é o
 * servidor: a Edge Function `mfa-recovery` confere o código com a chave de
 * serviço e apaga o fator pela Admin API.
 *
 * Isso significa que um código de recuperação **derruba a verificação em duas
 * etapas** em vez de conceder `aal2`. É menos elegante e mais honesto: quem usou
 * o código volta a ter só a senha, e a tela diz isso na cara. O contrário —
 * elevar para `aal2` com um segredo guardado em papel — daria ao papel o mesmo
 * poder do celular, sem o mesmo cuidado.
 */
export function DesafioMfa({ aoElevar }: { aoElevar: () => void }) {
  const { signOut } = useAuth();
  const [codigo, setCodigo] = useState("");
  const [modo, setModo] = useState<"totp" | "recuperacao">("totp");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [fatorId, setFatorId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    supabase.auth.mfa.listFactors().then(({ data }) => {
      const verificado = data?.totp?.find((f) => f.status === "verified");
      setFatorId(verificado?.id ?? null);
    });
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, [modo]);

  const tamanhoEsperado = modo === "totp" ? 6 : 14; // XXXXX-XXXXX-XX

  async function verificar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      if (modo === "totp") {
        if (!fatorId) {
          setErro("Não encontramos seu aplicativo autenticador. Use um código de recuperação.");
          return;
        }
        const { error } = await supabase.auth.mfa.challengeAndVerify({
          factorId: fatorId,
          code: codigo.trim(),
        });
        if (error) {
          // A mensagem do provedor é genérica de propósito: dizer "código
          // expirado" e "código errado" com palavras diferentes entrega ao
          // atacante em qual dos dois ele está.
          setErro("Código inválido. Confira o aplicativo e tente de novo.");
          return;
        }
        aoElevar();
      } else {
        const { data, error } = await supabase.functions.invoke("mfa-recovery", {
          body: { action: "consume", code: codigo.trim().toUpperCase() },
        });
        if (error || !data?.ok) {
          setErro("Código de recuperação inválido ou já usado.");
          return;
        }
        // O fator morreu no servidor. A sessão local ainda acha que deve aal2;
        // recarregar é o jeito mais curto de reconciliar as duas versões.
        window.location.reload();
      }
    } finally {
      setEnviando(false);
      setCodigo("");
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="rounded-full bg-primary/10 p-3 mb-3">
            <ShieldCheck className="h-6 w-6 text-primary" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold text-foreground">Verificação em duas etapas</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {modo === "totp"
              ? "Digite o código de 6 dígitos do seu aplicativo autenticador."
              : "Digite um dos códigos de recuperação que você guardou."}
          </p>
        </div>

        <form onSubmit={verificar} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="codigo-mfa">{modo === "totp" ? "Código" : "Código de recuperação"}</Label>
            <Input
              id="codigo-mfa"
              ref={inputRef}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              autoComplete={modo === "totp" ? "one-time-code" : "off"}
              inputMode={modo === "totp" ? "numeric" : "text"}
              placeholder={modo === "totp" ? "000000" : "XXXXX-XXXXX-XX"}
              maxLength={tamanhoEsperado}
              className="text-center tracking-widest text-lg"
              aria-describedby={erro ? "erro-mfa" : undefined}
              aria-invalid={!!erro}
            />
          </div>

          {erro && (
            <p id="erro-mfa" role="alert" className="text-sm text-destructive">
              {erro}
            </p>
          )}

          {modo === "recuperacao" && (
            <p role="note" className="text-xs text-muted-foreground border rounded-md p-3">
              Usar um código de recuperação <strong>desliga</strong> a verificação em duas
              etapas. Você entrará com a senha e poderá configurá-la de novo em Configurações.
            </p>
          )}

          <Button type="submit" className="w-full" disabled={enviando || codigo.length < 6}>
            {enviando ? "Verificando…" : "Verificar"}
          </Button>
        </form>

        <div className="mt-6 flex flex-col gap-2 text-sm">
          <button
            type="button"
            onClick={() => {
              setModo(modo === "totp" ? "recuperacao" : "totp");
              setCodigo("");
              setErro(null);
            }}
            className="inline-flex items-center justify-center gap-1.5 text-primary hover:underline"
          >
            {modo === "totp" ? (
              <>
                <LifeBuoy className="h-3.5 w-3.5" aria-hidden />
                Perdi o acesso ao aplicativo
              </>
            ) : (
              <>
                <KeyRound className="h-3.5 w-3.5" aria-hidden />
                Usar o aplicativo autenticador
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => signOut()}
            className="inline-flex items-center justify-center gap-1.5 text-muted-foreground hover:underline"
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden />
            Sair da conta
          </button>
        </div>
      </div>
    </div>
  );
}
